import POSButton from '@/components/POS/Common/POSButton';
import POSHeading from '@/components/POS/Common/POSHeading';
import POSInput from '@/components/POS/Common/POSInput';
import { Box, IconButton, Typography, Paper, Modal } from '@mui/material';
import { t } from 'i18next';
import { Close } from '@mui/icons-material';
import { CountryList, CountryListSchema } from '@/data/CountrylistTyped';
import POSPhoneField from '@/components/POS/Common/POSPhoneField';
import { useState } from 'react';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import { api } from '@/utils/Api/POS';

export type ReviewChannelType = 'EMAIL' | 'SMS' | 'EMAIL_AND_SMS';

interface InitialTestValueType {
    email: string | null;
    bccEmail: string | null;
    phoneNumber: string;
    countryCode: string;
    countryISOCode: string;
}

interface POSTrustpilotTestModelProps {
    openTestModel: boolean;
    handleCloseModel: () => void;
    reviewChannel: ReviewChannelType;
    isSaved: boolean;
}

function POSTrustpilotTestModel({
    openTestModel,
    handleCloseModel,
    reviewChannel,
    isSaved,
}: POSTrustpilotTestModelProps) {
    const [isTestLoading, setTestLoading] = useState(false);
    const [phoneLength, setPhoneLength] = useState<{ minLength: number; maxLength: number }>({
        minLength: 8,
        maxLength: 8,
    });

    const formik = useFormik<InitialTestValueType>({
        initialValues: {
            email: '',
            bccEmail: '',
            phoneNumber: '',
            countryCode: '+45',
            countryISOCode: 'DK',
        },
        enableReinitialize: true,
        validationSchema: Yup.object({
            email:
                reviewChannel === 'EMAIL' || reviewChannel === 'EMAIL_AND_SMS'
                    ? Yup.string().required(t('Common.EmailRequired')).email(t('Setting.InvalidEmailFormat'))
                    : Yup.string().nullable(),

            bccEmail: Yup.string().nullable().email(t('Setting.InvalidEmailFormat')),

            phoneNumber:
                reviewChannel === 'SMS' || reviewChannel === 'EMAIL_AND_SMS'
                    ? Yup.string()
                          .required(t('Marketing.SMSCampaignsTestPhoneRequired'))
                          .matches(
                              new RegExp(`^\\d{${phoneLength?.minLength},${phoneLength?.maxLength}}$`),
                              t('Customer.PhoneNumberTypeError'),
                          )
                          .typeError(t('Customer.PhoneNumberTypeError'))
                    : Yup.string().nullable(),

            countryCode:
                reviewChannel === 'SMS' || reviewChannel === 'EMAIL_AND_SMS'
                    ? Yup.string().required(`${t('Country')}${t('GiftCard.Code')} ${t('Customer.IsRequired')}`)
                    : Yup.string().nullable(),

            countryISOCode:
                reviewChannel === 'SMS' || reviewChannel === 'EMAIL_AND_SMS'
                    ? Yup.string().required(`${t('Country')} ${t('Customer.IsRequired')}`)
                    : Yup.string().nullable(),
        }),
        onSubmit: (values) => {
            callTestPostApi(values);
        },
    });

    const callTestPostApi = async (values: InitialTestValueType) => {
        if (isSaved) {
            toast.error(t('Common.PleaseSaveBeforeProceeding'), {
                style: {
                    zIndex: 100,
                },
            });
            return;
        }

        setTestLoading(true);
        try {
            await api.postApiTrustpilotSendTest({
                testEmail: values.email,
                testPhoneNumber: values.phoneNumber,
                testCountryCode: values.countryCode,
                channel: reviewChannel,
                bccEmail: values.bccEmail || null,
            });

            if (reviewChannel === 'EMAIL' || reviewChannel === 'EMAIL_AND_SMS') {
                toast.success(t('Marketing.EmailCampaignsTestEmailSent'), {
                    style: {
                        zIndex: 100,
                    },
                });
            }

            if (reviewChannel === 'SMS' || reviewChannel === 'EMAIL_AND_SMS') {
                toast.success(t('Marketing.SMSCampaignsTestSMSSent'), {
                    style: {
                        zIndex: 100,
                    },
                });
            }
            handleCloseModel();
        } catch (error) {
            console.error(t('Marketing.EmailCampaignsTestEmailError'), error);
            toast.error(t('Marketing.EmailCampaignsTestEmailError'), {
                style: {
                    zIndex: 100,
                },
            });
        } finally {
            setTestLoading(false);
        }
    };

    console.log(formik);

    return (
        <Modal
            disableAutoFocus
            open={openTestModel}
            onClose={handleCloseModel}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
            sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 9,
            }}
        >
            <Paper
                sx={{
                    position: 'relative',
                    maxWidth: { xs: '90%', md: 'auto' },
                    minWidth: '50%',
                    width: { xs: '90%', md: 'auto' },
                    maxHeight: '90%',
                    p: 4,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    bgcolor: '#fff',
                    borderRadius: 4,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                }}
            >
                <IconButton onClick={handleCloseModel} sx={{ position: 'absolute', top: 8, right: 8 }}>
                    <Close sx={{ color: '#a2a2a2' }} />
                </IconButton>
                <POSHeading sx={{ mb: 3 }} text={`${t('Common.Test')} ${t('Integration.Trustpilot')}`} />

                {(reviewChannel === 'EMAIL' || reviewChannel === 'EMAIL_AND_SMS') && (
                    <>
                        <Typography fontWeight={700}>{t('Common.Email')}</Typography>
                        <POSInput
                            value={formik.values.email}
                            placeholder={t('Common.Email')}
                            onChange={(e) => formik.setFieldValue('email', e.target.value)}
                            onBlur={() => formik.setFieldTouched('email', true)}
                            sx={{ width: '100%' }}
                            type="email"
                            error={formik.touched.email && Boolean(formik.errors.email)}
                        />
                        <Typography fontWeight={400} color="error">
                            {formik.errors.email}
                        </Typography>

                        <Typography fontWeight={700}>
                            {t('Marketing.BccEmail')} ({t('Common.Optional')})
                        </Typography>
                        <POSInput
                            value={formik.values.bccEmail ?? ''}
                            placeholder={t('Marketing.BccEmail')}
                            onChange={(e) => formik.setFieldValue('bccEmail', e.target.value)}
                            onBlur={() => formik.setFieldTouched('bccEmail', true)}
                            sx={{ width: '100%' }}
                            type="email"
                            error={formik.touched.bccEmail && Boolean(formik.errors.bccEmail)}
                        />
                        <Typography fontWeight={400} color="error">
                            {formik.errors.bccEmail}
                        </Typography>
                    </>
                )}

                {(reviewChannel === 'SMS' || reviewChannel === 'EMAIL_AND_SMS') && (
                    <>
                        <Typography fontWeight={700}>{t('Calendar.PhoneNumber')}</Typography>

                        <POSPhoneField
                            name="customerPhone"
                            placeholder={t('Calendar.PhoneNumber')}
                            value={{
                                country_code: formik.values.countryCode,
                                phone: formik.values.phoneNumber,
                                countryISOCode: formik.values.countryISOCode,
                            }}
                            onChange={(phoneNumber) => {
                                formik.setFieldValue('phoneNumber', phoneNumber);
                            }}
                            onCountryChange={(value, countryISOCode) => {
                                const newLength = CountryList[
                                    countryISOCode as keyof typeof CountryList
                                ] as CountryListSchema;
                                formik.setFieldValue('countryCode', value);
                                formik.setFieldValue('countryISOCode', countryISOCode);

                                let minLength, maxLength;

                                if (newLength?.minLength && newLength?.maxLength) {
                                    minLength = newLength?.minLength;
                                    maxLength = newLength?.maxLength;
                                } else {
                                    minLength = newLength?.phoneLength ?? 8;
                                    maxLength = newLength?.phoneLength ?? 8;
                                }

                                setPhoneLength({ minLength, maxLength });
                            }}
                        />
                        <Typography fontWeight={400} color="error">
                            {formik.errors.phoneNumber}
                        </Typography>
                    </>
                )}

                <Box
                    sx={{
                        position: 'sticky',
                        bottom: 2,
                        right: 0,
                        width: '100%',
                        height: 48,
                        display: 'flex',
                        justifyContent: 'flex-end',
                        bgcolor: '#fff',
                        zIndex: 10,
                        py: 1,
                    }}
                >
                    <POSButton
                        variant="save"
                        width="auto"
                        height={35}
                        title={t('POS.Send')}
                        onClick={formik.handleSubmit}
                        loading={isTestLoading}
                    />
                </Box>
            </Paper>
        </Modal>
    );
}

export default POSTrustpilotTestModel;
